import {
  useGetAllStudentsQuery,
  useAddStudentMutation,
  useGetStudentByIdQuery,
  useUpdateStudentMutation,
  useDeleteStudentMutation,
  useUploadStudentPhotoMutation,
  useUploadStudentDobDocumentMutation,
  useUpdateStudentStatusMutation,
} from "../api/studentApi";
import { StudentResponseType } from "../types/studentResponse";
import { StudentPayloadType } from "../types/studentRequest";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";
import { useState } from "react";
import { showToast } from "../../../../core/utils/alert";

export const useStudent = (params?: {
  size?: number;
  search?: string;
  page?: number;
  center_id?: number;
  course_id?: number;
  stage_id?: number;
  session_id?: number;
  batch_id?: number;
  status?: string;
  fee_type?: string;
  gender?: string;
  sort_by?: string;
  sort_order?: string;
  marks?: boolean;
}) => {
  const { data, isLoading, isError, error } = useGetAllStudentsQuery(
    params || {}
  );

  const [
    addStudentMutation,
    { isLoading: isAdding, isError: isAddError, error: addError },
  ] = useAddStudentMutation();

  const [
    updateStudentMutation,
    { isLoading: isUpdating, isError: isUpdateError, error: updateError },
  ] = useUpdateStudentMutation();

  const [
    updateStudentStatusMutation,
    {
      isLoading: isUpdatingStatus,
      isError: isUpdateStatusError,
      error: updateStatusError,
    },
  ] = useUpdateStudentStatusMutation();

  const [
    deleteStudentMutation,
    { isLoading: isDeleting, isError: isDeleteError, error: deleteError },
  ] = useDeleteStudentMutation();

  const [
    uploadStudentPhotoMutation,
    { isLoading: isUploading, error: uploadError },
  ] = useUploadStudentPhotoMutation();

  const [uploadStudentDobDocumentMutation] =
    useUploadStudentDobDocumentMutation();

  // Status management state
  const [statusUpdating, setStatusUpdating] = useState<Record<string, boolean>>(
    {}
  );
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [isDeactivateFromDelete, setIsDeactivateFromDelete] = useState(false);

  const students: StudentResponseType[] = data?.data?.items || [];

  const isLoadingState = isLoading;
  const errorState = error;
  const isErrorState = isError;

  const errorMessage = isErrorState ? getApiErrorMessage(errorState) : null;

  const paginationData = {
    totalPages: data?.data?.totalPages,
    pageSize: data?.data?.pageSize,
    totalItems: data?.data?.totalItems,
    currentPage: data?.data?.currentPage,
  };

  const addNewStudent = async (payload: StudentPayloadType) => {
    try {
      const response = await addStudentMutation(payload).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Failed to add new student:--->", error);
      return {
        success: false,
        error: getApiErrorMessage(error),
      };
    }
  };

  const useGetStudentById = (id: string) => {
    // console.log("id", id);
    const { data } = useGetStudentByIdQuery({ id }, { skip: !id });
    return data?.data.student;
  };

  const updateStudent = async (id: string, payload: StudentPayloadType) => {
    try {
      const response = await updateStudentMutation({ id, payload }).unwrap();
      return { success: true, data: response };
    } catch (error) {
      console.error("Failed to update student:--->", error);
      return {
        success: false,
        error: getApiErrorMessage(error),
      };
    }
  };

  const deleteStudent = async (id: string) => {
    try {
      const response = await deleteStudentMutation({ id }).unwrap();
      return { success: true, data: response };
    } catch (error: any) {
      if (error.status === 409) {
        return {
          success: false,
          error: "Associated records exist",
          status: 409,
        };
      }
      return {
        success: false,
        error: getApiErrorMessage(error) || "Failed to delete student",
      };
    }
  };

  const updateStudentStatus = async (id: string, status: string) => {
    try {
      const response = await updateStudentStatusMutation({
        id,
        status,
      }).unwrap();
      return { success: true, data: response };
    } catch (error) {
      return {
        success: false,
        error: isUpdateStatusError
          ? getApiErrorMessage(updateStatusError)
          : "Failed to update student status",
      };
    }
  };

  const deactivateStudent = async (id: string) => {
    return updateStudentStatus(id, "inactive");
  };

  const activateStudent = async (id: string) => {
    return updateStudentStatus(id, "active");
  };

  // Enhanced status management functions
  const handleActivate = async (studentId: number | string) => {
    const id = studentId.toString();
    setSelectedStudentId(id);
    setShowActivateModal(true);
  };

  const handleDeactivate = async (studentId: number | string) => {
    const id = studentId.toString();
    setStatusUpdating((prev) => ({ ...prev, [id]: true }));

    try {
      const result = await deactivateStudent(id);
      if (result.success) {
        showToast({
          title: "Success",
          text: "Student deactivated successfully",
          icon: "success",
        });
      } else {
        showToast({
          title: "Error",
          text: result.error || "Failed to deactivate student",
          icon: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Error",
        text: "An unexpected error occurred",
        icon: "error",
      });
    } finally {
      setStatusUpdating((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleDeleteClick = (id: string | number) => {
    setSelectedStudentId(id.toString());
    setShowDeleteModal(true);
    setIsDeactivateFromDelete(true);
  };

  const handleDeactivateClick = (id: string | number) => {
    setSelectedStudentId(id.toString());
    setShowDeactivateModal(true);
  };

  const uploadStudentPhoto = async (id: string, file: File) => {
    try {
      const response = await uploadStudentPhotoMutation({ id, file }).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Failed to upload student photo:--->", error);
      return {
        success: false,
        error: uploadError
          ? getApiErrorMessage(uploadError)
          : "Failed to upload photo",
      };
    }
  };

  const uploadStudentDobDocument = async (id: string, file: File) => {
    try {
      const response = await uploadStudentDobDocumentMutation({
        id,
        file,
      }).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Failed to upload student DOB document:--->", error);
      return {
        success: false,
        error: getApiErrorMessage(error) || "Failed to upload DOB document",
      };
    }
  };

  const getStudentState = () => ({
    students,
    isLoading: isLoadingState,
    error: errorMessage,
  });

  // Status management state getters and setters
  const getStatusManagementState = () => ({
    statusUpdating,
    selectedStudentId,
    showDeleteModal,
    showDeactivateModal,
    showActivateModal,
    isDeactivateFromDelete,
  });

  return {
    getStudentState,
    useGetStudentById,
    addNewStudent,
    updateStudent,
    deleteStudent,
    deactivateStudent,
    activateStudent,
    updateStudentStatus,
    uploadStudentPhoto,
    uploadStudentDobDocument,
    isAdding,
    isUpdating,
    isUpdatingStatus,
    isDeleting,
    isUploading,
    addError: isAddError ? getApiErrorMessage(addError) : null,
    updateError: isUpdateError ? getApiErrorMessage(updateError) : null,
    updateStatusError: isUpdateStatusError
      ? getApiErrorMessage(updateStatusError)
      : null,
    deleteError: isDeleteError ? getApiErrorMessage(deleteError) : null,
    uploadError: uploadError ? getApiErrorMessage(uploadError) : null,
    paginationData,
    // Enhanced status management functions
    handleActivate,
    handleDeactivate,
    handleDeleteClick,
    handleDeactivateClick,
    // Status management state
    statusUpdating,
    setStatusUpdating,
    selectedStudentId,
    setSelectedStudentId,
    showDeleteModal,
    setShowDeleteModal,
    showDeactivateModal,
    setShowDeactivateModal,
    showActivateModal,
    setShowActivateModal,
    isDeactivateFromDelete,
    setIsDeactivateFromDelete,
    getStatusManagementState,
  };
};
